import { estimateMutationScopeId } from '@/api/mutations/estimates/hydrateEstimateCaches';
import useRemoveEstimateAppliance from '@/api/mutations/estimates/useRemoveEstimateAppliance';
import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import { ProductSpecCategory } from '@/gql/graphql';
import AutoSaveForm from '@/ui/components/autoSaveForm/AutoSaveForm';
import ConfirmDeleteModal from '@/ui/components/confirm-delete-modal/ConfirmDeleteModal';
import LineItemRow from '@/ui/components/lineItemRow/LineItemRow';
import { Button, Field, RawInput, H4, Prose, Text } from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import MarkdownCore from '@payaca/components/markdownLabel/MarkdownCore';
import { SortableList } from '@payaca/dnd';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  useOptimisticRemoveEstimateLineItem,
  useOptimisticReorderEstimateLineItems,
  useOptimisticUpdateEstimateSection,
} from '../api/mutations';
import { EstimateLineItem } from '../types';
import { ApplianceThumb } from './applianceDisplay';

type SectionAppliance = NonNullable<EstimateLineItem['appliance']>;

type SectionEditorProps = {
  projectId: string;
  estimateId: string;
  sectionId: string;
  handleConfirmDelete: (sectionToDeleteId: string) => void;
  onAddLineItem: (sectionId: string) => void;
  onEditLineItem: (lineItemId: string) => void;
  onAddAppliance: (category: ProductSpecCategory, sectionId: string) => void;
};

/**
 * An Appliance's derived cost lines grouped into a product card: header (image,
 * spec name, Remove), the linked Items nested as clickable rows, and a footer
 * with the card total.
 */
const ApplianceCard: FC<{
  appliance: SectionAppliance;
  lines: EstimateLineItem[];
  onEditLineItem: (lineItemId: string) => void;
  onRemove: () => void;
}> = ({ appliance, lines, onEditLineItem, onRemove }) => {
  const spec = appliance.productSpec;
  const totalValue = lines.reduce(
    (sum, li) => sum + (li.totalIncTax?.value ?? 0),
    0
  );
  const currency = lines[0]?.totalIncTax.currency;

  return (
    <div
      role="group"
      aria-label={`Appliance: ${spec.name}`}
      className="mx-3.5 mb-3 overflow-hidden rounded-xl border border-gray-200"
    >
      <div className="flex items-center gap-3 p-3.5">
        <ApplianceThumb imageUrl={spec.image?.url} size="sm" />
        <div className="min-w-0 flex-1">
          <H4 truncate>{spec.name}</H4>
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="flex-shrink-0"
          aria-label={`Remove ${spec.name}`}
          onClick={onRemove}
        >
          <UntitledIcon name="x.3" className="size-4" />
        </Button>
      </div>
      <div className="divide-y border-t">
        {lines.map((lineItem) => (
          <button
            key={lineItem.id}
            type="button"
            onClick={() => onEditLineItem(lineItem.id)}
            className="flex w-full cursor-pointer items-center gap-3 bg-white px-3.5 py-2.5 text-left hover:bg-gray-50"
          >
            <div className="min-w-0 flex-1">
              {lineItem.internalReference && (
                <H4 truncate>{lineItem.internalReference}</H4>
              )}
              {lineItem.description?.trim() && (
                <Prose className="truncate [&_p]:m-0 [&_p]:inline">
                  <MarkdownCore markdown={lineItem.description} />
                </Prose>
              )}
            </div>
            <div className="flex-shrink-0 whitespace-nowrap text-right">
              <Text level="supporting">
                {lineItem.quantity} x{' '}
                <CurrencyValue {...lineItem.unitPriceExcTax} />
              </Text>
              <Text>
                <CurrencyValue {...lineItem.totalIncTax} />
              </Text>
            </div>
          </button>
        ))}
      </div>
      <div className="flex items-center justify-end border-t px-3.5 py-2.5">
        {currency && (
          <Text as="span">
            <strong>
              <CurrencyValue value={totalValue} currency={currency} />
            </strong>
          </Text>
        )}
      </div>
    </div>
  );
};

const SectionEditor: FC<SectionEditorProps> = (props) => {
  const {
    projectId,
    estimateId,
    sectionId,
    handleConfirmDelete,
    onAddLineItem,
    onEditLineItem,
    onAddAppliance,
  } = props;

  /**
   * queries
   */
  const { estimate } = useGetProjectEstimate(projectId, estimateId);
  const section = estimate?.sections.find((s) => s.id === sectionId);

  // Which appliance categories the "Add …" buttons offer, keyed off the
  // estimate's categories. A mixed-category estimate offers both sets; a
  // general estimate carries neither category and so offers neither.
  const estimateCategories = estimate?.categories ?? [];
  const isSolar = estimateCategories.includes('SOLAR');
  const isHeatPump = estimateCategories.includes('HEAT_PUMP');
  const applianceAddCategories: {
    category: ProductSpecCategory;
    label: string;
  }[] = [
    ...(isHeatPump
      ? [
          { category: 'HEAT_PUMP' as const, label: 'Add heat pump' },
          { category: 'CYLINDER' as const, label: 'Add water cylinder' },
        ]
      : []),
    ...(isSolar
      ? [
          { category: 'SOLAR_PANEL' as const, label: 'Add solar panel' },
          { category: 'INVERTER' as const, label: 'Add inverter' },
          { category: 'BATTERY' as const, label: 'Add battery' },
        ]
      : []),
  ];

  /**
   * state
   */
  const [sectionToDeleteId, setSectionToDeleteId] = useState<string | null>(
    null
  );
  const [applianceToRemove, setApplianceToRemove] =
    useState<SectionAppliance | null>(null);

  /**
   * mutations
   */
  const updateEstimateSection = useOptimisticUpdateEstimateSection({
    projectId,
    estimateId,
  });
  const reorderEstimateLineItems = useOptimisticReorderEstimateLineItems({
    projectId,
    estimateId,
  });
  const removeEstimateLineItem = useOptimisticRemoveEstimateLineItem({
    projectId,
    estimateId,
  });
  const removeEstimateAppliance = useRemoveEstimateAppliance({
    projectId,
    estimateId,
    options: { scope: { id: estimateMutationScopeId(estimateId) } },
  });

  /**
   * form state
   */
  const methods = useForm({
    defaultValues: {
      title: section?.heading ?? '',
    },
  });

  /**
   * callbacks
   */
  const handleLineItemDragEnd = (
    _activeItem: EstimateLineItem,
    _destinationIndex: number,
    items: EstimateLineItem[]
  ) => {
    // Only loose (non-Appliance) items are sortable. Appliance-derived lines
    // keep their positions up front, but the mutation needs every line id in
    // the section, so prepend them to the reordered loose ids.
    const applianceLineIds = (section?.lineItems ?? [])
      .filter((li) => li.appliance)
      .map((li) => li.id);
    reorderEstimateLineItems.mutate({
      sectionId,
      lineItemIds: [...applianceLineIds, ...items.map((li) => li.id)],
    });
  };

  // Defensive: the parent map only renders us for sections that exist in
  // the cache, so this should never fall through in practice.
  if (!section) return null;

  const lineItems = section.lineItems;

  // Group an Appliance's derived lines into a product card (first-seen order);
  // a section usually holds one Appliance but the model allows several.
  // Manually-added lines (no Appliance) list separately below.
  type ApplianceGroup = {
    appliance: SectionAppliance;
    lines: EstimateLineItem[];
  };
  const applianceGroups: ApplianceGroup[] = [];
  const groupByApplianceId = new Map<string, ApplianceGroup>();
  const looseItems: EstimateLineItem[] = [];
  for (const lineItem of lineItems) {
    if (lineItem.appliance) {
      const id = lineItem.appliance.id;
      let group = groupByApplianceId.get(id);
      if (!group) {
        group = { appliance: lineItem.appliance, lines: [] };
        groupByApplianceId.set(id, group);
        applianceGroups.push(group);
      }
      group.lines.push(lineItem);
    } else {
      looseItems.push(lineItem);
    }
  }
  const hasApplianceCards = applianceGroups.length > 0;

  return (
    <>
      <div className="flex flex-col min-h-full">
        <AutoSaveForm
          methods={methods}
          onSubmit={({ title }) => {
            updateEstimateSection.mutate({ id: sectionId, heading: title });
          }}
          className="px-3.5 pb-3.5 z-30"
        >
          <Field name="title">
            <Field.Label>Section title</Field.Label>

            <RawInput sizeVariant="sm" {...methods.register('title')} />
          </Field>
        </AutoSaveForm>

        {hasApplianceCards && (
          <div className="pt-1">
            {applianceGroups.map((group) => (
              <ApplianceCard
                key={group.appliance.id}
                appliance={group.appliance}
                lines={group.lines}
                onEditLineItem={onEditLineItem}
                onRemove={() => setApplianceToRemove(group.appliance)}
              />
            ))}
          </div>
        )}

        <div className="flex-1 min-h-0">
          <div className="px-3.5 pb-2">
            <H4 id={`estimate-section-line-items-label-${sectionId}`}>
              {hasApplianceCards
                ? `Other items in this section (${looseItems.length})`
                : `Section items (${lineItems.length})`}
            </H4>
          </div>

          <SortableList
            id={`estimate-section-line-items-${sectionId}`}
            labelledBy={`estimate-section-line-items-label-${sectionId}`}
            className="flex flex-col"
            items={looseItems}
            onDragEnd={handleLineItemDragEnd}
            lockAxis
            renderItem={(lineItem) => (
              <LineItemRow
                lineItem={lineItem}
                unitPriceExcTax={lineItem.unitPriceExcTax}
                totalIncTax={lineItem.totalIncTax}
                onClick={() => onEditLineItem(lineItem.id)}
                onDelete={() =>
                  removeEstimateLineItem.mutate({ id: lineItem.id })
                }
              />
            )}
          />

          <div className="px-3.5 py-4 sticky bottom-0 z-30 bg-white flex flex-col gap-2">
            <Button
              variant="tertiary"
              size="sm"
              className="w-full"
              onClick={() => onAddLineItem(sectionId)}
            >
              <UntitledIcon name="plus.3" className="size-4" />
              {hasApplianceCards ? 'Add other item' : 'Add item'}
            </Button>
            {/* The data model allows several Appliances per section (e.g. from
                auto-population), but the builder only adds one per section: hide
                the add buttons once this section already holds an appliance.
                The categories offered follow the estimate's own categories. */}
            {!hasApplianceCards &&
              applianceAddCategories.map(({ category, label }) => (
                <Button
                  key={category}
                  variant="tertiary"
                  size="sm"
                  className="w-full"
                  onClick={() => onAddAppliance(category, sectionId)}
                >
                  <UntitledIcon name="plus.3" className="size-4" />
                  {label}
                </Button>
              ))}
          </div>
        </div>

        <div className="px-3.5 py-4 border-t">
          <Button
            variant="tertiary"
            colour="negative"
            size="sm"
            className="w-full"
            onClick={() => setSectionToDeleteId(sectionId)}
          >
            Remove section
          </Button>
        </div>
      </div>
      <ConfirmDeleteModal
        isOpen={sectionToDeleteId !== null}
        onClose={() => setSectionToDeleteId(null)}
        onConfirmDelete={() => {
          if (!sectionToDeleteId) return;
          handleConfirmDelete(sectionToDeleteId);
          setSectionToDeleteId(null);
        }}
        entityName={{ singular: 'section', plural: 'sections' }}
        destructiveVerb="remove"
      />
      <ConfirmDeleteModal
        isOpen={applianceToRemove !== null}
        onClose={() => setApplianceToRemove(null)}
        onConfirmDelete={() => {
          if (!applianceToRemove) return;
          removeEstimateAppliance.mutate({
            estimateId,
            applianceId: applianceToRemove.id,
          });
          setApplianceToRemove(null);
        }}
        entityName={{ singular: 'appliance', plural: 'appliances' }}
        destructiveVerb="remove"
      />
    </>
  );
};

export default SectionEditor;
